import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { TRYON_COLORS } from "../colors";
import { useI18n } from "../i18n";
import type { Translations } from "../i18n/en";
import LanguageToggle from "../components/LanguageToggle";

function formatDate(ts: number, locale: string): string {
  return new Date(ts).toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function linkUrl(code: string): string {
  return `${location.origin}/t/${code}`;
}

function formatUsd(amount: number): string {
  return amount < 0.005 ? "< $0.01" : `$${amount.toFixed(2)}`;
}

function shortDevice(userAgent: string | null, t: Translations): string {
  if (!userAgent) return t.admin.unknownDevice;
  if (/iPhone/i.test(userAgent)) return "iPhone";
  if (/iPad/i.test(userAgent)) return "iPad";
  if (/Android/i.test(userAgent)) return "Android";
  if (/Macintosh/i.test(userAgent)) return "Mac";
  if (/Windows/i.test(userAgent)) return "Windows";
  return t.admin.otherDevice;
}

function PromptEditor() {
  const { t } = useI18n();
  const setting = useQuery(api.settings.getTryOnPrompt);
  const save = useMutation(api.settings.setTryOnPrompt);
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (setting === undefined) return null;
  const value = draft ?? setting.prompt;
  const dirty = value !== setting.prompt;

  const handleSave = async () => {
    await save({ prompt: value });
    setDraft(null);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <details className="rounded-softer bg-white p-4 md:p-5 shadow-card mb-8">
      <summary className="cursor-pointer font-medium text-charcoal select-none">
        {t.admin.generationPrompt}
        <span className="ml-2 text-xs font-normal text-charcoal-muted">
          {t.admin.generationPromptNote}
        </span>
      </summary>
      <textarea
        value={value}
        onChange={(e) => setDraft(e.target.value)}
        rows={4}
        className="mt-3 w-full rounded-soft border border-linen-200 bg-linen-50 px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-sage-400"
      />
      <div className="mt-2 flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || !value.trim()}
          className="min-h-[44px] rounded-soft bg-sage-500 px-5 text-sm font-medium text-white transition-colors hover:bg-sage-600 disabled:opacity-40"
        >
          {saved ? t.admin.saved : t.admin.savePrompt}
        </button>
        {value !== setting.default && (
          <button
            onClick={() => setDraft(setting.default)}
            className="min-h-[44px] rounded-soft px-3 text-sm text-charcoal-muted hover:text-charcoal"
          >
            {t.admin.resetToDefault}
          </button>
        )}
      </div>
    </details>
  );
}

function ColorsEditor() {
  const { t } = useI18n();
  const available = useQuery(api.settings.getAvailableColors);
  const save = useMutation(api.settings.setAvailableColors);

  if (available === undefined) return null;
  // null = setting never saved → every color is available
  const enabled = available ?? TRYON_COLORS.map((c) => c.name);

  const toggle = (name: string) => {
    const next = enabled.includes(name)
      ? enabled.filter((n) => n !== name)
      : [...enabled, name];
    save({ colors: next });
  };

  return (
    <details className="rounded-softer bg-white p-4 md:p-5 shadow-card mb-8">
      <summary className="cursor-pointer font-medium text-charcoal select-none">
        {t.admin.availableColors}
        <span className="ml-2 text-xs font-normal text-charcoal-muted">
          {t.admin.colorsShown(enabled.length, TRYON_COLORS.length)}
        </span>
      </summary>
      <div className="mt-3 flex flex-wrap gap-2">
        {TRYON_COLORS.map(({ name, hex }) => {
          const isOn = enabled.includes(name);
          return (
            <button
              key={name}
              type="button"
              onClick={() => toggle(name)}
              className={`flex min-h-[44px] items-center gap-2 rounded-full border px-3 text-sm transition-colors ${
                isOn
                  ? "border-sage-500 bg-white ring-2 ring-sage-400 text-charcoal"
                  : "border-linen-300 bg-linen-50 text-charcoal-muted opacity-60 hover:opacity-100"
              }`}
            >
              <span
                className="h-5 w-5 rounded-full border border-black/10"
                style={{ backgroundColor: hex }}
              />
              {t.colorNames[name] ?? name}
              <span className="text-xs">{isOn ? "✓" : "—"}</span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-charcoal-muted">
        {t.admin.colorsNote}
      </p>
    </details>
  );
}

function LinkDetail({ code }: { code: string }) {
  const { t, locale } = useI18n();
  const detail = useQuery(api.links.getDetail, { code });
  if (detail === undefined) {
    return <p className="mt-3 text-sm text-charcoal-muted">{t.admin.loadingSessions}</p>;
  }
  if (detail === null) return null;

  return (
    <div className="mt-4 border-t border-linen-200 pt-4">
      <h3 className="mb-2 text-sm font-medium text-charcoal">
        {t.admin.sessionsHeading(detail.sessions.length)}
      </h3>
      {detail.sessions.length === 0 ? (
        <p className="text-xs text-charcoal-muted">{t.admin.nobodyOpened}</p>
      ) : (
        <ul className="space-y-1">
          {detail.sessions.map((s) => (
            <li
              key={s.sessionId}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-soft bg-linen-50 px-3 py-2 text-xs text-charcoal"
            >
              <span className="font-medium">{shortDevice(s.userAgent, t)}</span>
              <span className="text-charcoal-muted">{t.admin.firstSeen(formatDate(s.createdAt, locale))}</span>
              <span className="text-charcoal-muted">{t.admin.lastActive(formatDate(s.lastActiveAt, locale))}</span>
              <span>{t.admin.triesUsed(s.triesUsed)}</span>
              <span title={s.consentAt ? t.admin.consentedAt(formatDate(s.consentAt, locale)) : t.admin.noConsentRecorded}>
                {s.consentAt ? t.admin.consentYes : t.admin.consentNo}
              </span>
            </li>
          ))}
        </ul>
      )}

      {detail.images.length > 0 && (
        <>
          <h3 className="mt-4 mb-2 text-sm font-medium text-charcoal">
            {t.admin.generatedImages(detail.images.length)}
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {detail.images.map((img) => (
              <div key={img._id} className="relative">
                {img.url ? (
                  <a href={img.url} target="_blank" rel="noreferrer">
                    <img
                      src={img.url}
                      alt={t.admin.tryOnAlt}
                      className="aspect-[3/4] w-full rounded-soft object-cover border border-linen-200"
                    />
                  </a>
                ) : (
                  <div className="flex aspect-[3/4] items-center justify-center rounded-soft bg-linen-100 text-[10px] text-charcoal-muted">
                    {img.status}
                  </div>
                )}
                {img.tryOnColor && (
                  <span className="absolute bottom-1 left-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] text-white">
                    {t.colorNames[img.tryOnColor] ?? img.tryOnColor}
                  </span>
                )}
                {img.costUsd > 0 && (
                  <span className="absolute top-1 right-1 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] text-white">
                    {formatUsd(img.costUsd)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TriesEditor({
  linkId,
  maxTries,
  triesUsed,
}: {
  linkId: Id<"links">;
  maxTries: number | null;
  triesUsed: number;
}) {
  const { t } = useI18n();
  const updateMaxTries = useMutation(api.links.updateMaxTries);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = async () => {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed >= 1) {
      await updateMaxTries({ linkId, maxTries: parsed });
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          min={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditing(false);
          }}
          onBlur={commit}
          className="w-16 rounded-soft border border-linen-200 px-2 py-0.5 text-xs text-charcoal focus:outline-none focus:ring-2 focus:ring-sage-400"
        />
      </span>
    );
  }

  return (
    <button
      onClick={() => {
        setValue(String(maxTries ?? triesUsed + 5));
        setEditing(true);
      }}
      className="rounded-full border border-linen-200 px-2.5 py-0.5 text-xs text-charcoal transition-colors hover:bg-linen-100"
      title={t.admin.changeTryLimit}
    >
      {maxTries === null ? t.admin.triesNoLimit(triesUsed) : t.admin.triesOf(triesUsed, maxTries)}
      <span className="ml-1 text-charcoal-muted">✎</span>
    </button>
  );
}

export default function AdminPage() {
  const { t, locale } = useI18n();
  const [title, setTitle] = useState("");
  const [maxTries, setMaxTries] = useState("5");
  const [dressFile, setDressFile] = useState<File | null>(null);
  const [dressPreview, setDressPreview] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: Id<"links">; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const dressInputRef = useRef<HTMLInputElement>(null);

  const links = useQuery(api.links.list);
  const createLink = useMutation(api.links.create);
  const setActive = useMutation(api.links.setActive);
  const removeLink = useMutation(api.links.remove);
  const generateUploadUrl = useMutation(api.storage.generateUploadUrl);

  const selectDress = (file: File) => {
    setDressFile(file);
    setDressPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(file);
    });
  };

  const clearDress = () => {
    setDressFile(null);
    setDressPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
  };

  const handleCreate = async () => {
    if (!title.trim() || isCreating) return;
    setIsCreating(true);
    try {
      let dressStorageId: Id<"_storage"> | undefined;
      if (dressFile) {
        const uploadUrl = await generateUploadUrl();
        const response = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": dressFile.type },
          body: dressFile,
        });
        const { storageId } = await response.json();
        dressStorageId = storageId as Id<"_storage">;
      }

      const parsedTries = parseInt(maxTries, 10);
      const code = await createLink({
        title: title.trim(),
        maxTries: !Number.isNaN(parsedTries) && parsedTries >= 1 ? parsedTries : undefined,
        dressStorageId,
      });
      setTitle("");
      clearDress();
      await copyLink(code);
    } catch (error) {
      console.error("Failed to create link:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      await removeLink({ linkId: deleteTarget.id });
      setDeleteTarget(null);
    } catch (error) {
      console.error("Failed to delete link:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const copyLink = async (code: string) => {
    try {
      await navigator.clipboard.writeText(linkUrl(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      // Clipboard unavailable (e.g. non-HTTPS) — ignore, URL is visible anyway
    }
  };

  return (
    <div className="min-h-screen bg-linen-50">
      <div className="mx-auto max-w-3xl px-4 py-8 md:py-12">
        <header className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <a
              href="/"
              className="-ml-2 inline-flex min-h-[44px] items-center gap-1 rounded-soft px-2 text-sm text-charcoal-muted transition-colors hover:text-charcoal"
            >
              <span aria-hidden>←</span> {t.admin.backToGenerator}
            </a>
            <LanguageToggle className="-mr-2" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-charcoal">
            {t.admin.title}
          </h1>
          <p className="mt-1 text-charcoal-muted text-sm">
            {t.admin.subtitle}
          </p>
        </header>

        <PromptEditor />

        <ColorsEditor />

        {/* Create form */}
        <div className="rounded-softer bg-white p-4 md:p-5 shadow-card mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder={t.admin.titlePlaceholder}
              className="flex-1 min-h-[44px] rounded-soft border border-linen-200 bg-linen-50 px-4 py-2.5 text-charcoal placeholder:text-linen-400 focus:outline-none focus:ring-2 focus:ring-sage-400"
            />
            <label className="flex items-center gap-2 text-sm text-charcoal whitespace-nowrap">
              {t.admin.triesLabel}
              <input
                type="number"
                min={1}
                value={maxTries}
                onChange={(e) => setMaxTries(e.target.value)}
                className="w-20 min-h-[44px] rounded-soft border border-linen-200 bg-linen-50 px-3 text-charcoal focus:outline-none focus:ring-2 focus:ring-sage-400"
              />
            </label>
            <button
              onClick={handleCreate}
              disabled={!title.trim() || isCreating}
              className="min-h-[44px] rounded-soft bg-sage-500 px-6 py-2.5 font-medium text-white transition-colors hover:bg-sage-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isCreating ? t.admin.creating : t.admin.generateLink}
            </button>
          </div>

          {/* Optional preselected dress */}
          <div className="mt-3 flex items-center gap-3">
            {dressPreview ? (
              <>
                <img
                  src={dressPreview}
                  alt={t.admin.preselectedDressAlt}
                  className="h-16 w-12 rounded-soft object-cover border border-linen-200"
                />
                <button
                  onClick={clearDress}
                  className="min-h-[44px] text-sm text-charcoal-muted hover:text-charcoal"
                >
                  {t.admin.removeDress}
                </button>
              </>
            ) : (
              <button
                onClick={() => dressInputRef.current?.click()}
                className="min-h-[44px] rounded-soft border border-dashed border-linen-300 px-4 text-sm text-charcoal-muted transition-colors hover:border-sage-400 hover:text-charcoal"
              >
                {t.admin.preselectDress}
              </button>
            )}
            <input
              ref={dressInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) selectDress(file);
                e.target.value = "";
              }}
            />
          </div>
        </div>

        {/* Links list */}
        {links === undefined ? (
          <p className="text-charcoal-muted text-sm">{t.common.loading}</p>
        ) : links.length === 0 ? (
          <div className="rounded-softer border border-dashed border-linen-300 p-10 text-center text-charcoal-muted">
            {t.admin.noLinksYet}
          </div>
        ) : (
          <ul className="space-y-4">
            {links.map((link) => (
              <li
                key={link._id}
                className={`rounded-softer bg-white p-4 md:p-5 shadow-soft ${
                  link.isActive ? "" : "opacity-60"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0 flex gap-3">
                    {link.dressUrl && (
                      <img
                        src={link.dressUrl}
                        alt={t.admin.preselectedDressAlt}
                        className="h-16 w-12 shrink-0 rounded-soft object-cover border border-linen-200"
                      />
                    )}
                    <div className="min-w-0">
                      <h2 className="font-serif text-xl text-charcoal truncate">
                        {link.title}
                        {!link.isActive && (
                          <span className="ml-2 align-middle rounded-full bg-linen-200 px-2 py-0.5 text-xs font-sans text-charcoal-muted">
                            {t.admin.paused}
                          </span>
                        )}
                      </h2>
                      <a
                        href={linkUrl(link.code)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-sage-600 hover:underline break-all"
                      >
                        {linkUrl(link.code)}
                      </a>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <TriesEditor
                          linkId={link._id}
                          maxTries={link.maxTries}
                          triesUsed={link.triesUsed}
                        />
                        <span className="text-xs text-charcoal-muted">
                          {t.admin.sessionCount(link.sessionCount)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-charcoal-muted">
                        {t.admin.createdAt(formatDate(link.createdAt, locale))}
                        {" · "}
                        {link.tryOnCount === 0
                          ? t.admin.noTryOnsYet
                          : t.admin.tryOnsCompleted(link.completedCount, link.tryOnCount)}
                        {link.lastActivityAt
                          ? ` · ${t.admin.lastActivity(formatDate(link.lastActivityAt, locale))}`
                          : ""}
                        {link.totalCostUsd > 0
                          ? ` · ${t.admin.generationCost(formatUsd(link.totalCostUsd))}`
                          : ""}
                      </p>
                      {link.colorsTried.length > 0 && (
                        <p className="mt-1 text-xs text-charcoal-muted">
                          {t.admin.colorsTried(
                            link.colorsTried.map((c) => t.colorNames[c] ?? c).join(", ")
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      onClick={() => setActive({ linkId: link._id, isActive: !link.isActive })}
                      className="min-h-[44px] rounded-soft border border-linen-200 px-3 py-2 text-sm text-charcoal transition-colors hover:bg-linen-100"
                    >
                      {link.isActive ? t.admin.pause : t.admin.resume}
                    </button>
                    <button
                      onClick={() => copyLink(link.code)}
                      className="min-h-[44px] rounded-soft border border-linen-200 px-4 py-2 text-sm text-charcoal transition-colors hover:bg-linen-100"
                    >
                      {copiedCode === link.code ? t.admin.copied : t.admin.copyLink}
                    </button>
                    <button
                      onClick={() => setDeleteTarget({ id: link._id, title: link.title })}
                      className="min-h-[44px] rounded-soft border border-linen-200 px-3 py-2 text-sm text-red-700 transition-colors hover:border-red-200 hover:bg-red-50"
                    >
                      {t.admin.deleteLink}
                    </button>
                  </div>
                </div>
                {link.recentThumbnails.length > 0 && (
                  <div className="mt-3 flex gap-2">
                    {link.recentThumbnails.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={t.admin.recentTryOnAlt}
                        className="h-16 w-16 rounded-soft object-cover border border-linen-200"
                      />
                    ))}
                  </div>
                )}
                <button
                  onClick={() =>
                    setExpandedCode(expandedCode === link.code ? null : link.code)
                  }
                  className="mt-3 min-h-[44px] text-sm text-sage-600 hover:underline"
                >
                  {expandedCode === link.code ? t.admin.hideDetails : t.admin.sessionsAndImages}
                </button>
                {expandedCode === link.code && <LinkDetail code={link.code} />}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Delete confirmation */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
          onClick={() => {
            if (!isDeleting) setDeleteTarget(null);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium text-charcoal mb-2">{t.admin.deleteLinkTitle}</h3>
            <p className="text-sm text-charcoal-muted mb-4">
              {t.admin.deleteLinkMessage(deleteTarget.title)}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                className="min-h-[44px] px-4 py-2 text-sm font-medium text-charcoal hover:bg-linen-100 rounded-lg transition-colors disabled:opacity-40"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-40"
              >
                {isDeleting ? t.admin.deletingLink : t.common.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
